import { useState } from "react";
import { Link } from "react-router-dom";
import type { Divergencia } from "@/api/contrato";
import { useSessao } from "@/sessao/useSessao";
import { useDivergencias, useResolverDivergencia } from "@/api/consultas";
import { textoDoErro } from "@/api/problema";
import { PilulaDeSituacao } from "@/componentes/PilulaDeSituacao";
import { Botao } from "@/componentes/Botao";
import { useAvisos } from "@/componentes/Avisos";
import { Confirmacao, type PedidoDeConfirmacao } from "@/componentes/Confirmacao";
import { dataHora, faz, idCurto } from "@/formato";
import { rotaDoBoleto } from "@/rotas";

/*
 * Divergências — doc 06 §4.3.
 *
 * ┌──────────────────────────────────────────────────────────────────────────┐
 * │ COMPARAÇÃO LADO A LADO, E RESOLUÇÃO NUNCA AUTOMÁTICA (doc 05 §7).       │
 * │                                                                          │
 * │ A conciliação REGISTRA e não corrige: divergência é sintoma de defeito,  │
 * │ e autocorreção silenciosa esconde o defeito enquanto continua produzindo │
 * │ dado errado.                                                             │
 * │                                                                          │
 * │ "Resolver" aqui significa "uma pessoa olhou e tratou" — e é isso que a   │
 * │ auditoria registra. Não muda a situação do boleto.                       │
 * └──────────────────────────────────────────────────────────────────────────┘
 */
const TIPOS: Readonly<Record<string, string>> = {
  situacao: "Situação diferente",
  desconhecido_no_provider: "Desconhecido no provider",
  conflito_sem_adocao: "Conflito sem adoção",
  titulos_multiplos: "Títulos múltiplos",
};

export function Divergencias() {
  const { cliente, estado, pode } = useSessao();
  const avisos = useAvisos();

  const [pedido, setPedido] = useState<PedidoDeConfirmacao | null>(null);
  const [alvo, setAlvo] = useState<Divergencia | null>(null);

  const { data, error, isPending } = useDivergencias(cliente, estado.fase === "dentro");
  const resolver = useResolverDivergencia(cliente);
  const podeOperar = pode("operador");

  if (isPending) {
    return <div aria-hidden="true" className="h-32 rounded-cartao bg-superficie" />;
  }

  if (error) {
    return (
      <p role="alert" className="rounded-cartao border border-falha/40 bg-falha/10 px-4 py-3 text-falha">
        {textoDoErro(error)}
      </p>
    );
  }

  const abertas = (data?.divergencias ?? []).filter((d) => !d.resolvida_em);

  if (abertas.length === 0) {
    return (
      <p className="text-texto-2">Nenhuma divergência aberta.</p>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      {abertas.map((divergencia) => (
        <article
          key={divergencia.id}
          className="rounded-cartao border border-fio bg-superficie p-3"
        >
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className="rotulo">{TIPOS[divergencia.tipo] ?? divergencia.tipo}</span>
                <span className="text-texto-2">·</span>
                <span className="truncate text-corpo text-texto">
                  {divergencia.sistema_nome}
                </span>
              </div>

              {/*
                Lado a lado: é a leitura que o operador precisa fazer. Ver "o
                nosso diz X, o deles diz Y" numa linha só é o que transforma a
                divergência em decisão.
              */}
              <div className="mt-2 flex flex-wrap items-center gap-3">
                <div className="flex items-center gap-2">
                  <span className="rotulo">nosso</span>
                  <PilulaDeSituacao situacao={divergencia.situacao_nossa} />
                </div>

                <span aria-hidden="true" className="text-texto-3">
                  ×
                </span>

                <div className="flex items-center gap-2">
                  <span className="rotulo">provider</span>
                  {divergencia.situacao_provider ?? divergencia.provider_situacao ? (
                    <span className="dados rounded-full border border-fio-forte bg-superficie-2 px-2 py-[2px] text-rotulo uppercase tracking-[0.08em] text-texto-2">
                      {divergencia.situacao_provider ?? divergencia.provider_situacao}
                    </span>
                  ) : (
                    /*
                      Sem situação do provider é o caso `desconhecido_no_provider`:
                      ele não conhece o título. Diferente de "diz outra coisa", e
                      o diagnóstico é outro.
                    */
                    <span className="text-[12px] text-texto-3">não conhece o título</span>
                  )}
                </div>
              </div>

              <div className="mt-2 flex flex-wrap gap-3 text-[12px] text-texto-2">
                <Link
                  to={rotaDoBoleto(divergencia.boleto_id)}
                  title={divergencia.boleto_id}
                  className="text-esferografica underline-offset-2 hover:underline"
                >
                  boleto <span className="dados">{idCurto(divergencia.boleto_id)}</span>
                </Link>
                <span>
                  {/*
                    Vezes é o que separa oscilação de defeito persistente: uma
                    divergência vista trinta vezes não é um webhook atrasado.
                  */}
                  vista <span className="dados">{divergencia.vezes}</span>×
                </span>
                <span title={divergencia.primeiro_em}>
                  primeiro há <span className="dados">{faz(divergencia.primeiro_em)}</span>
                </span>
                <span title={dataHora(divergencia.visto_em)}>
                  última há <span className="dados">{faz(divergencia.visto_em)}</span>
                </span>
              </div>
            </div>

            {podeOperar && (
              <Botao
                tom="secundario"
                onClick={() => {
                  setAlvo(divergencia);
                  setPedido({
                    severidade: "destrutiva",
                    titulo: "Marcar esta divergência como tratada?",
                    corpo:
                      "A situação do boleto não muda; sem a causa corrigida, a conciliação reabre.",
                    verbo: "Marcar como tratada",
                  });
                }}
              >
                Marcar como tratada
              </Botao>
            )}
          </div>
        </article>
      ))}

      <Confirmacao
        pedido={pedido}
        aoCancelar={() => setPedido(null)}
        aoConfirmar={async () => {
          if (alvo === null) return;
          await resolver.mutateAsync(alvo.id);
          setPedido(null);
          avisos.feito("Divergência marcada como tratada");
        }}
      />
    </div>
  );
}
